"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { TokenPayload } from "@/src/types/auth";
import { apiFetch, MOBILE_API } from "@/src/services/api";

interface AuthContextValue {
  token: string | null;
  userId: number | null;
  phoneNumber: string | null;
  name: string | null;
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeToken(token: string): TokenPayload | null {
  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(atob(payload));
    return decoded as TokenPayload;
  } catch {
    return null;
  }
}

function isTokenValid(token: string): boolean {
  const payload = decodeToken(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 > Date.now();
}

const STORAGE_KEY = "buyer_token";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isTokenValid(stored)) {
      const payload = decodeToken(stored)!;
      setToken(stored);
      setUserId(payload.userId);
      setPhoneNumber(payload.phoneNumber);
    } else if (stored) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Fetch buyer name once authenticated
  useEffect(() => {
    if (!userId || !token) return;
    apiFetch<{ success: boolean; data: { name?: string } }>(
      `${MOBILE_API}/users/${userId}`,
      { token }
    ).then((res) => {
      if (res.success && res.data?.name) setName(res.data.name);
    }).catch(() => {});
  }, [userId, token]);

  const login = useCallback((newToken: string) => {
    const payload = decodeToken(newToken);
    if (!payload) return;
    localStorage.setItem(STORAGE_KEY, newToken);
    setToken(newToken);
    setUserId(payload.userId);
    setPhoneNumber(payload.phoneNumber);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUserId(null);
    setPhoneNumber(null);
    setName(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        userId,
        phoneNumber,
        name,
        isAuthenticated: token !== null,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
